import type { Control } from 'react-hook-form';
import { EducationSlide, FormStep, SteppedModal } from '@components/ui/SteppedModal';
import { Alert, Box } from '@mui/material';

import { useAddonOnboarding } from '../hooks/useAddonOnboarding';
import type { AddonOnboardingConfig, OnboardingStepConfig, OnboardingStepKind } from '../types';

/** Map a step config to its renderer. Exhaustive over OnboardingStepConfig['kind']. */
function renderStepContent(step: OnboardingStepConfig, control: Control): React.ReactNode {
  switch (step.kind) {
    case 'education':
      return <EducationSlide slide={step.slide} />;
    case 'form':
      return <FormStep config={step.form} control={control} />;
    default: {
      // Compile-time guard: a new step kind must add a case above.
      const _exhaustive: never = step;
      return _exhaustive;
    }
  }
}

interface AddonOnboardingFlowProps {
  config: AddonOnboardingConfig;
  /** Seed values (e.g. the current user's email) merged over the empty form defaults. */
  initialValues?: Record<string, string>;
  /** Persist a form step's captured values on advance (e.g. a registration mutation). */
  onSubmit?: (values: Record<string, string>) => Promise<void> | void;
  /** Called with captured form values when the user finishes the last step. */
  onComplete?: (values: Record<string, string>) => void;
  /** Called when the user dismisses the flow. */
  onClose?: () => void;
  /** Called when the visible step changes (next/back). */
  onStepChange?: (info: {
    stepIndex: number;
    stepKind: OnboardingStepKind;
    direction: 'next' | 'back';
  }) => void;
}

/**
 * Renders a config-driven onboarding flow on the shared SteppedModal shell.
 * The config supplies an ordered list of education / form steps; this component
 * maps each step kind to its renderer and delegates navigation, form state
 * (react-hook-form + zod), and submission to useAddonOnboarding.
 */
export const AddonOnboardingFlow: React.FC<AddonOnboardingFlowProps> = ({
  config,
  initialValues,
  onSubmit,
  onComplete,
  onClose,
  onStepChange,
}) => {
  const {
    stepIndex,
    currentStep,
    totalSteps,
    control,
    footer,
    submitError,
    handleClose,
  } = useAddonOnboarding({ config, initialValues, onSubmit, onComplete, onClose, onStepChange });

  return (
    <SteppedModal
      branding={config.branding}
      onClose={handleClose}
      totalSteps={totalSteps}
      currentStep={stepIndex}
      footer={footer}
      contentKey={`step-${stepIndex}`}
    >
      {renderStepContent(currentStep, control)}
      {submitError && (
        <Box sx={{ px: 3, pb: 2.5 }}>
          <Alert severity="error">{submitError}</Alert>
        </Box>
      )}
    </SteppedModal>
  );
};
